import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classnames from 'classnames';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { ShopItemType } from '@sorare/core/src/__generated__/globalTypes';
import {
  ShopItemsSorting,
  SortingOption,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { isType } from '@sorare/core/src/lib/gql';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import type { ShopItemLogo } from '#src/components/shopItems/ShopItemDialog/types.ts';

import { FilterBy } from './FilterBy/index.tsx';
import { DefaultListing } from './Listings/DefaultListing/index.tsx';
import { SortBy, SortValues } from './SortBy/index.tsx';
import type {
  ShopItemPickerQuery,
  ShopItemPickerQueryVariables,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 2 })`
  margin-bottom: var(--triple-unit);
`;
const SortAndFilterContainer = styled.div`
  display: flex;
  gap: var(--unit);
`;
const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--double-unit);
  @media ${tabletAndAbove} {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
  }
  @media ${laptopAndAbove} {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
  }
  &.inDialog {
    grid-template-columns: repeat(2, 1fr);
  }
`;

type SortAndFilter = {
  sort: SortValues;
  filter: ShopItemType[];
};

const getSortingValues = (sort: SortValues) => {
  switch (sort) {
    case SortValues.LATEST_UNLOCKED:
      return {
        type: ShopItemsSorting.UNLOCKED,
        direction: SortingOption.DESC,
      };
    case SortValues.NEWEST:
      return {
        type: ShopItemsSorting.DATE,
        direction: SortingOption.DESC,
      };
    case SortValues.PRICE_HIGH_LOW:
      return {
        type: ShopItemsSorting.PRICE,
        direction: SortingOption.DESC,
      };
    case SortValues.PRICE_LOW_HIGH:
      return {
        type: ShopItemsSorting.PRICE,
        direction: SortingOption.ASC,
      };
    default:
      return {
        type: ShopItemsSorting.DATE,
        direction: SortingOption.DESC,
      };
  }
};

const SHOP_ITEM_PICKER_QUERY = gql`
  query ShopItemPickerQuery(
    $cursor: String
    $sort: ShopItemsSortInput!
    $types: [ShopItemType!]
    $unlocked: Boolean
    $sport: Sport!
  ) {
    shopItems(
      first: 12
      after: $cursor
      types: $types
      unlockedOnly: $unlocked
      sortType: $sort
      sport: $sport
    ) {
      nodes {
        ... on ShopItemInterface {
          id
          myBalance
          pictureUrl
        }
        ...DefaultListing_shopItem
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      totalCount
    }
    currentUser {
      slug
      userSportProfile: anyUserSportProfile(sport: $sport) {
        id
        ...DefaultListing_userSportProfile
      }
    }
  }
  ${DefaultListing.fragments.shopItem}
  ${DefaultListing.fragments.userSportProfile}
` as TypedDocumentNode<ShopItemPickerQuery, ShopItemPickerQueryVariables>;

type Props = {
  onSelect?: (shopItem: ShopItemLogo) => void;
  types: ShopItemType[];
  inDialog?: boolean;
  hideSort?: boolean;
};

export const ShopItemPicker = ({
  onSelect,
  types,
  inDialog,
  hideSort,
}: Props) => {
  const [sortAndFilter, setSortAndFilter] = useState<SortAndFilter>({
    sort: SortValues.LATEST_UNLOCKED,
    filter: types,
  });
  const { sport } = useSportContext();
  const typesToQuery = sortAndFilter.filter.length
    ? sortAndFilter.filter
    : types;
  const { data, loading, loadMore } = usePaginatedQuery(
    SHOP_ITEM_PICKER_QUERY,
    {
      variables: {
        sort: getSortingValues(sortAndFilter.sort),
        types: typesToQuery,
        unlocked: true,
        sport: sport || Sport.FOOTBALL,
      },
      connection: 'ClubShopItemConnection',
    }
  );

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        cursor: data?.shopItems?.pageInfo.endCursor,
        types: typesToQuery,
      });
    },
    Boolean(data?.shopItems?.pageInfo?.hasNextPage),
    loading
  );

  useEffect(() => {
    setSortAndFilter(v => {
      const hasDefaultSort = [
        SortValues.LATEST_UNLOCKED,
        SortValues.NEWEST,
      ].includes(v.sort);

      return {
        sort: hasDefaultSort ? SortValues.LATEST_UNLOCKED : v.sort,
        filter: types,
      };
    });
  }, [types]);

  const excludeNoCooldownJerseyFromInventory = (
    item: ShopItemPickerQuery['shopItems']['nodes'][number]
  ) => {
    if (isType(item, 'JerseyShopItem')) {
      return item.myLimitResetAt !== null;
    }
    return true;
  };

  const items = data?.shopItems.nodes.filter(
    excludeNoCooldownJerseyFromInventory
  );
  return (
    <Root>
      <SBHorizontal gap={0}>
        <BodyS color="var(--c-white)">
          <FormattedMessage
            id="ClubShop.Items.Number"
            defaultMessage="{number} items"
            values={{ number: data?.shopItems.totalCount || 0 }}
          />
        </BodyS>
        {!hideSort && (
          <SortAndFilterContainer>
            <SortBy
              value={sortAndFilter.sort}
              onChange={value =>
                setSortAndFilter(prevValue => ({
                  ...prevValue,
                  sort: value,
                }))
              }
            />
            <FilterBy
              types={types}
              values={sortAndFilter.filter}
              onChange={values => {
                setSortAndFilter(prevValue => ({
                  ...prevValue,
                  filter: values,
                }));
              }}
            />
          </SortAndFilterContainer>
        )}
      </SBHorizontal>
      {!data ? (
        <LoadingIndicator small />
      ) : (
        <>
          <ItemsGrid className={classnames({ inDialog })}>
            {items?.map(item => {
              if (item.myBalance === 0) {
                return null;
              }

              const listingProps = {
                onSelect: onSelect ? () => onSelect(item) : undefined,
                inDialog,
              };
              return (
                <DefaultListing
                  key={item.id}
                  item={item}
                  userSportProfile={data.currentUser?.userSportProfile}
                  {...listingProps}
                />
              );
            })}
          </ItemsGrid>
          <InfiniteScrollLoader />
        </>
      )}
    </Root>
  );
};
